/**
 * SurveyExitSummary — Auto-generated exit conference summary.
 *
 * Aggregates all findings by CfC condition, severity-ranked, with
 * pattern analysis and condition-level alerts. Printable as PDF
 * via browser print.
 *
 * Route: /visits/:visitId/survey/exit-summary
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Printer, AlertTriangle, Camera, BarChart3 } from 'lucide-react'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { getSurvey, type SurveySession } from '../../lib/services/survey'
import { surveyStore } from '../../lib/offline/surveyStore'
import { generateExitSummary, type ExitSummary, type FindingSummaryItem } from '../../lib/exitSummary'

export function SurveyExitSummary() {
  const { visitId } = useParams<{ visitId: string }>()
  const [, setSession] = useState<SurveySession | null>(null)
  const [summary, setSummary] = useState<ExitSummary | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!visitId) return
    const load = async () => {
      setIsLoading(true)
      const s = await getSurvey(visitId)
        .then((s) => s ?? surveyStore.getSessionByVisit(visitId).then((os) => os ?? null))
        .catch(() => surveyStore.getSessionByVisit(visitId).then((os) => os ?? null))
      if (!s) { setIsLoading(false); return }
      setSession(s)

      const [findings, photos, documents] = await Promise.all([
        surveyStore.getFindingsBySurvey(s.survey_id),
        surveyStore.getPhotosBySurvey(s.survey_id),
        surveyStore.getDocumentsBySurvey(s.survey_id),
      ])

      const items: FindingSummaryItem[] = findings.map((f) => ({
        finding_id: f.finding_id,
        domain: f.domain,
        standard_ref: f.standard_ref,
        severity: f.severity,
        observation: f.observation,
        area: f.area,
        photo_ids: f.photo_ids,
      }))

      setSummary(generateExitSummary(items, {
        facilityName: s.facility_name || 'Facility',
        surveyDate: new Date(s.started_at).toLocaleDateString(),
        framework: s.framework || 'ASC',
        totalPhotos: photos.length,
        totalDocuments: documents.length,
      }))
      setIsLoading(false)
    }
    load()
  }, [visitId])

  if (isLoading) {
    return (
      <FieldAppShell eyebrow="Survey" title="Exit Conference Summary" backTo=".." backLabel="Back">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]" />
        </div>
      </FieldAppShell>
    )
  }

  if (!summary) {
    return (
      <FieldAppShell eyebrow="Survey" title="Exit Conference Summary" backTo=".." backLabel="Back">
        <div className="px-4 py-6 text-center space-y-3">
          <p className="text-sm text-slate-500">No survey data available.</p>
          <Link to=".." className="btn-ghost text-sm">Back</Link>
        </div>
      </FieldAppShell>
    )
  }

  const sevColors = { 1: 'bg-blue-100 text-blue-700', 2: 'bg-amber-100 text-amber-700', 3: 'bg-red-100 text-red-700' }

  return (
    <FieldAppShell eyebrow="Survey" title="Exit Conference Summary" backTo=".." backLabel="Back">
    <div className="space-y-4 px-4 py-6 print:px-0 print:py-0">
      {/* Header — hidden on print except title */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-2">
          <Link to=".." className="text-slate-400 hover:text-slate-600">
            <ArrowLeft size={20} />
          </Link>
          <h2 className="section-title">Exit Conference Summary</h2>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="btn-ghost text-xs py-1.5 px-3"
        >
          <Printer size={14} className="inline mr-1" /> Print / PDF
        </button>
      </div>

      {/* Print header */}
      <div className="hidden print:block text-center space-y-1 pb-4 border-b border-slate-300">
        <h2 className="text-xl font-bold">Exit Conference Summary</h2>
        <p className="text-sm">{summary.facilityName}</p>
        <p className="text-xs text-slate-500">Survey Date: {summary.surveyDate} | Framework: {summary.framework}</p>
      </div>

      {/* Overview stats */}
      <div className="app-surface p-4 print:border print:border-slate-300">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-2xl font-bold text-[var(--brand-primary)]">{summary.totalFindings}</p>
            <p className="text-[10px] text-slate-500 uppercase">Findings</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[var(--brand-primary)]">{summary.totalPhotos}</p>
            <p className="text-[10px] text-slate-500 uppercase">Photos</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[var(--brand-primary)]">{summary.totalDocuments}</p>
            <p className="text-[10px] text-slate-500 uppercase">Documents</p>
          </div>
        </div>
      </div>

      {/* Severity distribution */}
      <div className="app-surface p-4 space-y-2 print:border print:border-slate-300">
        <div className="flex items-center gap-2">
          <BarChart3 size={14} className="text-slate-500" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Severity Distribution</span>
        </div>
        <div className="flex gap-2">
          {summary.severityDistribution.map(({ severity, label, count }) => (
            <div key={severity} className={`flex-1 rounded-lg p-2 text-center ${sevColors[severity]}`}>
              <p className="text-lg font-bold">{count}</p>
              <p className="text-[10px] font-bold uppercase">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Condition-level alerts */}
      {summary.conditionAlerts.length > 0 && (
        <div className="space-y-2">
          {summary.conditionAlerts.map((alert) => (
            <div
              key={alert.cfcSection}
              className={`app-surface p-4 space-y-1 border-l-4 print:border print:border-slate-300 ${
                alert.severity === 'critical' ? 'border-red-500 bg-red-50' : 'border-amber-400 bg-amber-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle size={14} className={alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'} />
                <span className="text-xs font-bold">
                  {alert.severity === 'critical' ? 'CONDITION-LEVEL ALERT' : 'PATTERN WARNING'}: {alert.cfcTitle}
                </span>
              </div>
              <p className="text-[11px] text-slate-700">{alert.description}</p>
            </div>
          ))}
        </div>
      )}

      {/* Findings by CfC condition */}
      {summary.cfcGroups.map((group) => (
        <div key={group.cfcSection} className="app-surface p-4 space-y-3 print:border print:border-slate-300 print:break-inside-avoid">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-slate-800">{group.cfcSection} — {group.cfcTitle}</span>
              <span className="ml-2 brand-chip text-[10px]">{group.domain}</span>
            </div>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sevColors[group.maxSeverity]}`}>
              {group.findings.length} finding{group.findings.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-2">
            {group.findings.map((f) => (
              <div key={f.finding_id} className="border-l-2 border-slate-200 pl-3 py-1">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${sevColors[f.severity]}`}>
                    {f.severity === 3 ? 'HIGH' : f.severity === 2 ? 'MOD' : 'LOW'}
                  </span>
                  <span className="text-[10px] text-slate-400">{f.area}</span>
                  <span className="text-[10px] font-mono text-slate-400">{f.standard_ref}</span>
                  {f.photo_ids.length > 0 && (
                    <span className="text-[10px] text-slate-400"><Camera size={10} className="inline" /> {f.photo_ids.length}</span>
                  )}
                </div>
                <p className="text-xs text-slate-700 mt-0.5 leading-relaxed">{f.observation}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Ungrouped findings */}
      {summary.ungroupedFindings.length > 0 && (
        <div className="app-surface p-4 space-y-3 print:border print:border-slate-300">
          <span className="text-xs font-bold text-slate-800">Other Findings</span>
          <div className="space-y-2">
            {summary.ungroupedFindings.map((f) => (
              <div key={f.finding_id} className="border-l-2 border-slate-200 pl-3 py-1">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${sevColors[f.severity]}`}>
                    {f.severity === 3 ? 'HIGH' : f.severity === 2 ? 'MOD' : 'LOW'}
                  </span>
                  <span className="text-[10px] text-slate-400">{f.area} — {f.domain}</span>
                </div>
                <p className="text-xs text-slate-700 mt-0.5">{f.observation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Domain coverage */}
      <div className="app-surface p-4 space-y-2 print:border print:border-slate-300">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Domain Coverage</span>
        <div className="grid grid-cols-2 gap-1.5">
          {summary.domainCoverage.map(({ domain, count }) => (
            <div key={domain} className="flex items-center justify-between bg-slate-50 rounded px-2 py-1.5 text-xs">
              <span className="font-bold text-slate-700">{domain}</span>
              <span className="text-slate-500">{count} finding{count !== 1 ? 's' : ''}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-[10px] text-slate-400 py-4 print:pt-8 print:border-t print:border-slate-300">
        <p>Generated {new Date().toLocaleString()} — ASC Survey Compliance Platform</p>
      </div>
    </div>
    </FieldAppShell>
  )
}
